import { FederalStatesDotStatus } from '@/components/FederalStatesDotsTile/FederalStatesDot.vue';
import {
    ExternalEventLocationVisibility,
    ExternalEventLocationWithAllocationsFragment,
    GetExternalEventLocationDotsDataNationalDocument,
} from '@/generated/graphql';
import { useQuery } from '@vue/apollo-composable';
import { computed, Ref, toValue } from 'vue';

const LOCATION_DOTS_CHUNK_SIZE = 4;

export function useExternalEventLocationDotsData(
    eventId: Ref<string | undefined>,
    variantId: Ref<string>,
    isNational: Ref<boolean>,
) {
    if (isNational.value) {
        const { result, loading } = useQuery(
            GetExternalEventLocationDotsDataNationalDocument,
            () => ({
                variantId: toValue(variantId),
                eventId: toValue(eventId)!,
            }),
            () => ({
                enabled: !!toValue(variantId) && !!toValue(eventId),
            }),
        );

        const locations = computed(() => result.value?.externalEventLocations?.nodes ?? []);
        const allocatedLocationsCount = computed(
            () =>
                locations.value.filter(
                    (loc) => loc.allocations.totalCount > 0 || loc.optionalAllocations.totalCount > 0,
                ).length,
        );

        function getLocationDotStatus(
            allocationCount: number,
            optionalAllocationCount: number,
            visibility?: ExternalEventLocationVisibility,
        ): FederalStatesDotStatus {
            if (allocationCount > 0 && optionalAllocationCount === 0) {
                switch (visibility) {
                    case ExternalEventLocationVisibility.Invisible:
                        return 'Occupied-Prio-3';
                    case ExternalEventLocationVisibility.Less:
                        return 'Occupied-Prio-2';
                    default:
                        return 'Occupied-Prio-1';
                }
            }
            if (optionalAllocationCount > 0 && allocationCount === 0) {
                switch (visibility) {
                    case ExternalEventLocationVisibility.Invisible:
                        return 'Optionally-Occupied-Prio-3';
                    case ExternalEventLocationVisibility.Less:
                        return 'Optionally-Occupied-Prio-2';
                    default:
                        return 'Optionally-Occupied-Prio-1';
                }
            }
            return 'Empty';
        }

        function mapForLocationDots(location: ExternalEventLocationWithAllocationsFragment, chunkIndex: number) {
            const allocationsCount = location.allocations.totalCount;
            const optionalAllocationsCount = location.optionalAllocations.totalCount;
            const visibility = location.visibilities ? location.visibilities.nodes[0].visibility : undefined;
            return {
                id: location.id,
                name: location.name,
                status: getLocationDotStatus(allocationsCount, optionalAllocationsCount, visibility),
                defaultTooltipPlacement:
                    chunkIndex < locations.value.length / LOCATION_DOTS_CHUNK_SIZE - 1
                        ? ('top' as const)
                        : ('bottom' as const),
            };
        }

        const locationChunks = computed(() => {
            const chunks = [];
            if (locations.value) {
                for (let i = 0; i < locations.value.length; i = i + LOCATION_DOTS_CHUNK_SIZE) {
                    const chunkIndex = i / LOCATION_DOTS_CHUNK_SIZE;
                    const chunkLocations = locations.value.slice(i, i + LOCATION_DOTS_CHUNK_SIZE).map((l) => {
                        return mapForLocationDots(l, chunkIndex);
                    });
                    chunks.push(chunkLocations);
                }
            }
            return chunks;
        });

        const showLocationsTile = computed(() => isNational.value);

        return {
            loading,
            locationChunks,
            locations,
            allocatedLocationsCount,
            showLocationsTile,
        };
    }
    return {
        loading: false,
        locationChunks: [],
        locations: [],
        allocatedLocationsCount: 0,
        showLocationsTile: false,
    };
}
